<template>
  <div style="width: 100%">
    <b-loading :active.sync="isLoading" :can-cancel="false" :is-full-page="true"></b-loading>
    <div class="tile is-ancestor">
      <div class="tile is-4 is-vertical is-parent">
        <div class="tile is-child box">
          <p class="title">General info</p>
          <table>
            <tr>
              <td>Name:</td>
              <td><strong>{{ gameName }}</strong></td>
            </tr>
            <tr>
              <td>Description:</td>
              <td><strong>{{ gameDescription }}</strong></td>
            </tr>
            <tr>
              <td>Game style:</td>
              <td><strong>{{ gameStyle }}</strong></td>
            </tr>
            <tr>
              <td>Game access:</td>
              <td><strong>{{ accessType | capitalize }}</strong></td>
            </tr>
            <tr>
              <td>Map:</td>
              <td><strong>{{ mapName }} ({{ markers.length }} points)</strong></td>
            </tr>
            <tr>
              <td>Pack:</td>
              <td><strong>{{ packName }}</strong></td>
            </tr>
            <tr>
              <td>Image:</td>
              <td>
                <b-img :height="100" :img="image" :width="200" style="padding-top: 10px"></b-img>
              </td>
            </tr>
          </table>
        </div>
        <div class="tile is-child box">
          <p class="title">Advanced</p>
          <table>
            <tr>
              <td>Use intro:</td>
              <td>
                <b-icon :icon="formatBooleanWithIcon(introTextId).icon"
                        :type="formatBooleanWithIcon(introTextId).class"
                        size="is-small"></b-icon>
              </td>
            </tr>
            <tr v-if="introTextId">
              <td>Intro:</td>
              <td><strong>{{ introTextName }}</strong>
              </td>
            </tr>

            <tr>
              <td>Show other teams:</td>
              <td>
                <b-icon :icon="formatBooleanWithIcon(showOtherTeams).icon"
                        :type="formatBooleanWithIcon(showOtherTeams).class"
                        size="is-small"></b-icon>
              </td>
            </tr>
            <tr>
              <td>Players can create teams:</td>
              <td>
                <b-icon :icon="formatBooleanWithIcon(playerCanAddTeams).icon"
                        :type="formatBooleanWithIcon(playerCanAddTeams).class"
                        size="is-small"></b-icon>
              </td>
            </tr>

            <tr>
              <td>Time attack:</td>
              <td>
                <div v-if="maxDurationInMinutes > 0">
                  <strong>{{ maxDurationInMinutes }} min</strong>
                </div>
                <b-icon v-if="maxDurationInMinutes === 0"
                        :icon="formatBooleanWithIcon(false).icon"
                        :type="formatBooleanWithIcon(false).class"
                        size="is-small"
                ></b-icon>
              </td>
            </tr>

            <tr>
              <td colspan="2">
                <hr/>
              </td>
            </tr>

            <tr>
              <td>Start Date:</td>
              <td><strong>{{ formatDateAndTime(gameStartDate) }}</strong></td>
            </tr>

            <tr>
              <td>End Date:</td>
              <td><strong>{{ formatDateAndTime(gameEndDate) }}</strong></td>
            </tr>
            <tr>
              <td>Game Over mode:</td>
              <td><strong>{{ formatGameOverMode(gameOverMode) }}</strong></td>
            </tr>
            <tr>
              <td>Accuracy:</td>
              <td><strong>{{ questionRadius }} meters</strong></td>
            </tr>
            <tr>
              <td>Leaderboard:</td>
              <td>
                <b-icon :icon="formatBooleanWithIcon(!displayLeaderboard).icon"
                        :type="formatBooleanWithIcon(!displayLeaderboard).class"
                        size="is-small"></b-icon>
              </td>
            </tr>
            <tr>
              <td>Play with teams:</td>
              <td>
                <b-icon :icon="formatBooleanWithIcon(playWithTeams).icon"
                        :type="formatBooleanWithIcon(playWithTeams).class"
                        size="is-small"></b-icon>
              </td>
            </tr>
            <tr>
              <td>Is always on:</td>
              <td>
                <b-icon :icon="formatBooleanWithIcon(isAlwaysOn).icon"
                        :type="formatBooleanWithIcon(isAlwaysOn).class"
                        size="is-small"></b-icon>
              </td>
            </tr>
          </table>
        </div>
      </div>
      <div class="tile is-parent">
        <div class="tile is-child box">
          <p class="title">Map and activities</p>
          <map-previewer ref="map_previewer" :center="center" :mapType="mapType" :markers="markers"
                         height="250"></map-previewer>
          <div style="padding-top: 20px"></div>
          <raw-activities-random-table
              :max-lines-selected="markers.length - 1"
              :random-raw-activities="rawActivities"
              :shuffle-display="false"
          ></raw-activities-random-table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import gamesService from '../../services/gamesService'
import displayHelper from '../../helpers/display-helper'
import BImg from '../single/BImg'
import MapPreviewer from '../single/MapPreviewer'
import RawActivitiesRandomTable from '../single/RawActivitiesRandomTable'
import textHelper from '../../helpers/text-helper'
import rawActivityHelper from '../../helpers/rawActivities-helper'
import gameStylesHelper from '../../enums/gameStyles'

const moment = require('moment')

export default {
  components: {
    BImg,
    MapPreviewer,
    RawActivitiesRandomTable,
  },
  props: {
    game: {},
    next: false,
    isEdit: false,
  },
  data() {
    return {
      isLoading: false,
      gameName: '',
      gameDescription: '',
      image: null,
      gameStyle: '',
      accessType: '',
      mapName: '',
      markers: [],
      packName: '',
      questionRadius: '',
      gameOverMode: '',
      displayLeaderboard: false,
      autoStop: false,
      introTextId: '',
      introTextName: '', // useful only for display
      playWithTeams: false,
      showOtherTeams: true,
      playerCanAddTeams: false,
      isAlwaysOn: false,
      gameStartDate: '',
      gameEndDate: '',
      maxDurationInMinutes: 0,
      rawActivities: [],

      center: {lat: 1.3581036, lng: 103.8329625},
      mapType: 'terrain',
    }
  },
  watch: {
    'next': function (next) {
      if (next) {
        // game creation
        this.tryToSubmitGameWithWizard()
      }
    }
  },
  created() {
    this.init()
  },
  filters: {
    capitalize(s) {
      return textHelper.capitalize(s)
    },
  },
  methods: {
    formatBooleanWithIcon: rawActivityHelper.formatBooleanWithIcon,
    init() {
      this.gameName = this.game.gameName
      this.gameDescription = this.game.gameDescription
      this.accessType = this.game.accessType
      this.packName = _.get(this.game, 'chosenPack.packName')
      this.mapName = _.get(this.game, 'map.mapName')
      this.markers = _.get(this.game, 'map.markers') ? _.get(this.game, 'map.markers') : []
      this.questionRadius = this.game.questionRadius
      this.gameOverMode = this.game.gameOverMode
      this.displayLeaderboard = this.game.isLeaderDisabled
      this.showOtherTeams = this.game.isShowOtherTeamsEnabled
      this.autoStop = this.game.autoStop
      this.playWithTeams = this.game.playWithTeams
      this.playerCanAddTeams = this.game.playerCanAddTeams
      this.isAlwaysOn = this.game.isAlwaysOn
      this.gameStartDate = this.game.gameStartDate
      this.gameEndDate = this.game.gameEndDate
      this.introTextId = this.game.introTextId
      this.introTextName = this.game.introTextName
      this.maxDurationInMinutes = this.game.maxDurationInMinutes

      // time attack mode: CLASSIC STYLE + MAX DURATION ==> TIME ATTACK STYLE
      if (this.game.gameStyle === gameStylesHelper.GAME_STYLES.CLASSIC && this.game.maxDurationInMinutes > 0) {
        this.game.gameStyle = gameStylesHelper.GAME_STYLES.TIME_ATTACK
      }

      this.gameStyle = this.game.gameStyle
      this.rawActivities = this.game.associationMarkersActivities

      if (this.game.existingLogo) {
        this.image = this.game.existingLogo
      } else {
        this.image = this.game.image
      }
    },
    formatDateAndTime(d) {
      return moment(d).format('DD/MM/YYYY HH:mm').toString()
    },
    formatGameOverMode(g) {
      return g === '' ? 'Infinity' : g
    },
    tryToSubmitGameWithWizard() {
      const parent = this

      // adding some extra info for game categorisation
      this.game.creationMode = 'Wizard'
      this.game.gameDisplayedName = this.game.gameName
      this.game.gameLogo = this.game.image

      this.game.maxDurationInMinutes = '' + this.game.maxDurationInMinutes

      delete this.game.introTextName

      console.log(this.game)

      this.isLoading = true

      if (!this.isEdit) {
        gamesService.createGameWithWizard(this.game).then(() => {
              this.isLoading = false
              this.$router.push({name: 'Games'})
            },
            (err) => {
              displayHelper.displayError(parent, 'Oops, there was an error while creating the game: ' + err.message)
              this.isLoading = false
            })
      } else {
        gamesService.updateGameWithWizard(this.game, this.game.idGame).then(() => {
              this.isLoading = false
              this.$router.push({name: 'Games'})
            },
            (err) => {
              displayHelper.displayError(parent, 'Oops, there was an error while updating the game: ' + err.message)
              this.isLoading = false
            })
      }
    }
  }
}
</script>
<style scoped>
td {
  padding-left: 5px;
  padding-right: 5px;
}
</style>
